/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageTemplate from '@page/iyagigo/page-template';
import VocabularyBookStudy from '@component/iyagigo/vocabulary-book/vocabulary-book-study';
import HeaderProgress from '@component/iyagigo/common/header-progress';
import TextBlock from '@component/common/block/text-block';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import {
  useVocabularyWorkbookBookQuery,
  useVocabularyWorkbookBookmarkPageQuery,
  useVocabularyWorkbookIncorrectNotePageQuery,
  useReviewVocabularyWorkbookPageQuery,
} from '@hook/iyagigo/useApi';
import { useVocabularyWorkbookSync } from '@hook/iyagigo/useStudent';
import { EApiLanguageCode, EApiVocabularyBookCategory, EApiVocabularyBookLevel } from '@datatype/iyagigo/api-type';
import { EVocabularyBookStudyType, EWorkbookItemType, TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { createVocabularyBookPath } from '@constant/iyagigo/route-path';

const VocabularyBookStudyPage: React.FC = () => {
  const dispatch = useTypedDispatch();
  const [searchParams] = useSearchParams();
  const vocabularyBookKey = searchParams.get('vocabularyBookKey');
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const vocabularyBookStudyType =
    useTypedSelector((state) => state.rootReducer?.vocabularyBookReducer?.vocabularyBookStudyType) ??
    EVocabularyBookStudyType.BOOK_STUDY;
  const vocabularyBookItemType =
    useTypedSelector((state) => state.rootReducer?.vocabularyBookReducer?.vocabularyBookItemType) ??
    EWorkbookItemType.BUNDLE;
  const isVocabularyBookNote =
    vocabularyBookStudyType === EVocabularyBookStudyType.BOOKMARK_NOTE ||
    vocabularyBookStudyType === EVocabularyBookStudyType.INCORRECT_NOTE;
  const isReviewStudy = vocabularyBookStudyType === EVocabularyBookStudyType.REVIEW;
  const isNoteSingle = isVocabularyBookNote && vocabularyBookItemType === EWorkbookItemType.SINGLE;
  const isRepeatStudy = vocabularyBookItemType === EWorkbookItemType.BUNDLE && isVocabularyBookNote;

  // ############### api ###############
  const apiLanguageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;
  // 재학습 큐 진입에 필요한 (카테고리 × 레벨) - 페르소나 토글 기준
  const vocabularyBookSubMenu = useTypedSelector(
    (state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu,
  ) as TIyagigoSubMenu['vocabulary-book'];
  // 시험단어집(TEST) 미노출 — 카테고리는 STUDY 고정. subMenu는 학습/복습 탭 축이라 카테고리를 결정하지 않음.
  // const vocabularyBookType =
  //   vocabularyBookSubMenu === 'vocabulary-book-study'
  //     ? EApiVocabularyBookCategory.STUDY
  //     : EApiVocabularyBookCategory.TEST;
  const vocabularyBookType = EApiVocabularyBookCategory.STUDY;
  const currentEnglishStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishStudyVocabularyBookLevel,
  );
  const currentJapaneseStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseStudyVocabularyBookLevel,
  );
  const currentLevel =
    apiLanguageCode === EApiLanguageCode.ENGLISH
      ? currentEnglishStudyVocabularyBookLevel
      : currentJapaneseStudyVocabularyBookLevel;

  const bookQuery = useVocabularyWorkbookBookQuery(vocabularyBookKey, {
    enabled: !isVocabularyBookNote && !isReviewStudy,
  });
  const bookmarkQuery = useVocabularyWorkbookBookmarkPageQuery(apiLanguageCode, isRepeatStudy ? true : undefined);
  const incorrectNoteQuery = useVocabularyWorkbookIncorrectNotePageQuery(
    apiLanguageCode,
    isRepeatStudy ? true : undefined,
  );
  const reviewQuery = useReviewVocabularyWorkbookPageQuery(
    apiLanguageCode,
    vocabularyBookType,
    currentLevel as EApiVocabularyBookLevel,
  );
  const workbooks = useMemo(() => {
    switch (vocabularyBookStudyType) {
      case EVocabularyBookStudyType.BOOKMARK_NOTE:
        return bookmarkQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      case EVocabularyBookStudyType.INCORRECT_NOTE:
        return incorrectNoteQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      case EVocabularyBookStudyType.REVIEW:
        return reviewQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
      default:
        return bookQuery.data?.data ?? [];
    }
  }, [vocabularyBookStudyType, bookmarkQuery.data, incorrectNoteQuery.data, reviewQuery.data, bookQuery.data]);

  const totalCount = workbooks.length;
  const { workbookIdx: currentIndex, setWorkbookIdx: setCurrentIndex, isReady } = useVocabularyWorkbookSync(workbooks);
  const isLoading = !isReady;

  const handleBackClick = () => {
    dispatch(
      modalActions.showDialogModal({
        type: 'workbook-quit',
        data: {
          mainMenu: 'vocabulary-book',
          // 현재 학습 중인 subMenu(시험준비/일반학습) 보존하여 vocabulary-book-home의 탭 유지
          subMenu: vocabularyBookSubMenu ?? undefined,
          // 노트에서 들어온 경우, quit 후 노트 목록으로 복귀
          nextPath: isVocabularyBookNote ? createVocabularyBookPath('note') : undefined,
        },
      }),
    );
  };

  return (
    <PageTemplate
      title=''
      mode='close'
      handleNavigationClick={handleBackClick}
      customHeader={
        isNoteSingle ? (
          <TextBlock
            text={vocabularyBookStudyType === EVocabularyBookStudyType.BOOKMARK_NOTE ? '저장한 단어' : '틀린 단어'}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: false } }}
          />
        ) : (
          <HeaderProgress currentProgress={currentIndex} totalProgress={totalCount} loading={isLoading} />
        )
      }>
      {(vocabularyBookKey || isVocabularyBookNote || isReviewStudy) && (
        <VocabularyBookStudy
          vocabularyBookKey={vocabularyBookKey ?? ''}
          workbooks={workbooks}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          isReady={isReady}
        />
      )}
    </PageTemplate>
  );
};

export default VocabularyBookStudyPage;
